<template>
  <div class="flex flex-col h-full min-h-0">
    <!-- Transcript -->
    <div ref="transcriptEl" class="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1" data-testid="ai-transcript">
      <!-- Restoring a stored conversation -->
      <div v-if="chat.isLoadingConversation.value"
        class="flex items-center justify-center gap-2 py-10 text-sm text-gray-400"
        data-testid="ai-loading-conversation">
        <i class="pi pi-spin pi-spinner"></i>
        Loading conversation…
      </div>

      <!-- Conversation load failure: inline, composer stays usable -->
      <div v-if="chat.loadError.value"
        class="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2.5 text-sm"
        data-testid="ai-load-error">
        <i class="pi pi-exclamation-triangle mt-0.5"></i>
        <span>{{ chat.loadError.value }} You can still start a new message below.</span>
      </div>

      <!-- Empty state -->
      <div v-if="chat.messages.value.length === 0 && !chat.isStreaming.value && !chat.isLoadingConversation.value"
        class="h-full flex flex-col items-center justify-center text-center py-10">
        <i class="pi pi-sparkles text-4xl text-primary/30 mb-4"></i>
        <p class="text-gray-600 font-medium mb-1">Ask anything about your school data</p>
        <p class="text-sm text-gray-400 mb-5">The assistant can look up students, results and billing, and show you where things live in the app.</p>
        <div class="flex flex-wrap justify-center gap-2">
          <button v-for="suggestion in suggestions" :key="suggestion" type="button"
            class="px-3 py-1.5 text-sm rounded-full border border-gray-200 text-gray-600 hover:border-primary hover:text-primary transition-colors"
            @click="onSend(suggestion)">
            {{ suggestion }}
          </button>
        </div>
      </div>

      <!-- Messages -->
      <div v-for="(msg, i) in chat.messages.value" :key="i" class="flex"
        :class="msg.role === 'user' ? 'justify-end' : 'justify-start'">
        <div v-if="msg.role === 'user'" class="max-w-[85%] flex flex-col items-end gap-1">
          <div
            class="bg-primary text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm whitespace-pre-wrap break-words"
            data-testid="ai-user-message">{{ msg.text }}</div>
          <!-- What rode along with this turn (metadata only) -->
          <div v-if="msg.attachments && msg.attachments.length" class="flex flex-wrap gap-1 justify-end">
            <span v-for="(file, f) in msg.attachments" :key="`${file.name}-${f}`"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-primary/10 text-primary border border-primary/20"
              data-testid="ai-attachment-badge">
              <i class="pi pi-paperclip text-[10px]"></i>
              {{ file.name }}
            </span>
          </div>
        </div>
        <div v-else class="flex flex-col gap-1.5 items-start max-w-[90%]">
          <!-- Replayed tool chips: a resumed answer looks like it did live -->
          <div v-if="msg.toolActivity && msg.toolActivity.length" class="flex flex-wrap gap-1.5">
            <span v-for="(tool, t) in msg.toolActivity" :key="`${tool.name}-${t}`"
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border"
              :class="toolChipClass(tool.status)"
              data-testid="ai-tool-chip">
              <i :class="toolChipIcon(tool.status)"></i>
              {{ tool.label }}
            </span>
          </div>
          <div
            class="bg-gray-50 border border-gray-100 rounded-2xl rounded-bl-md px-4 py-2.5 text-sm text-gray-800 ai-markdown"
            data-testid="ai-assistant-message"
            v-html="renderAiMarkdown(msg.text)"></div>
        </div>
      </div>

      <!-- Live turn: tool chips + incremental text -->
      <div v-if="chat.isStreaming.value" class="flex flex-col gap-2 items-start" data-testid="ai-streaming-block">
        <div v-if="chat.toolActivity.value.length" class="flex flex-wrap gap-1.5">
          <span v-for="(tool, i) in chat.toolActivity.value" :key="`${tool.name}-${i}`"
            class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border"
            :class="toolChipClass(tool.status)"
            data-testid="ai-tool-chip">
            <i :class="toolChipIcon(tool.status)"></i>
            {{ tool.label }}
          </span>
        </div>
        <div v-if="chat.streamingText.value"
          class="max-w-[90%] bg-gray-50 border border-gray-100 rounded-2xl rounded-bl-md px-4 py-2.5 text-sm text-gray-800 whitespace-pre-wrap break-words"
          data-testid="ai-streaming-text">{{ chat.streamingText.value }}<span class="ai-cursor"></span></div>
        <div v-else-if="!chat.toolActivity.value.length" class="flex items-center gap-2 text-gray-400 text-sm px-1">
          <i class="pi pi-spin pi-spinner"></i>
          Thinking…
        </div>
      </div>

      <!-- Inline error (E2: never a blank screen) -->
      <div v-if="chat.error.value"
        class="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2.5 text-sm"
        data-testid="ai-error">
        <i class="pi pi-exclamation-triangle mt-0.5"></i>
        <span>{{ chat.error.value }}</span>
      </div>

      <div v-if="chat.finishReason.value === 'length'" class="text-xs text-amber-600 px-1" data-testid="ai-truncated-note">
        The answer was cut short because it reached the length limit.
      </div>
    </div>

    <!-- Decisions waiting on the person (the floating widget fills this) -->
    <slot name="above-composer" />

    <!-- Composer -->
    <div class="pt-3 border-t border-gray-100 mt-2">
      <!-- Files staged for the NEXT turn -->
      <div v-if="pendingFiles.length" class="flex flex-wrap gap-1.5 mb-2" data-testid="ai-pending-attachments">
        <span v-for="(file, f) in pendingFiles" :key="`${file.name}-${f}`"
          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-gray-100 border border-gray-200 text-gray-700"
          data-testid="ai-pending-attachment">
          <i class="pi pi-paperclip text-[11px]"></i>
          {{ file.name }}
          <span class="text-gray-400">{{ prettySize(file.size) }}</span>
          <button type="button" class="text-gray-400 hover:text-red-500"
            :aria-label="`Remove ${file.name}`"
            data-testid="ai-remove-attachment"
            @click="removeFile(f)">
            <i class="pi pi-times text-[10px]"></i>
          </button>
        </span>
      </div>
      <p v-if="attachmentError" class="text-xs text-red-500 mb-2" data-testid="ai-attachment-error">
        {{ attachmentError }}
      </p>

      <div class="flex items-end gap-2">
        <input ref="fileInput" type="file" class="hidden" multiple
          :accept="ACCEPTED_MIMES.join(',')"
          data-testid="ai-file-input"
          @change="onFilesPicked" />
        <Button v-tooltip.top="attachmentLimits" icon="pi pi-paperclip" text severity="secondary"
          :disabled="chat.isStreaming.value"
          :aria-label="`Attach files (${attachmentLimits})`"
          data-testid="ai-attach" @click="fileInput?.click()" />
        <Textarea v-model="draft" :disabled="chat.isStreaming.value" autoResize rows="1"
          class="flex-1 max-h-40 text-sm" placeholder="Ask the assistant…"
          data-testid="ai-input"
          @keydown.enter.exact.prevent="onSend()" />
        <Button v-if="chat.isStreaming.value" icon="pi pi-stop-circle" severity="danger" outlined
          label="Stop" data-testid="ai-stop" @click="chat.abort()" />
        <Button v-else icon="pi pi-send" :disabled="!draft.trim()"
          label="Send" data-testid="ai-send" @click="onSend()" />
      </div>
      <p class="text-xs text-gray-400 mt-2 text-center" data-testid="ai-attachment-limits">
        <i class="pi pi-paperclip text-[10px]"></i> {{ attachmentLimits }}
      </p>
      <p class="text-xs text-gray-400 mt-1 text-center" data-testid="ai-disclaimer">
        AI can make mistakes — verify important information.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { UseAiChatReturn, AiToolStatus, AiChatAttachmentUpload } from '@/composables/useAiChat';
import { renderAiMarkdown } from '@/utils/aiMarkdown';
import {
  AI_ATTACHMENT_LIMITS,
  attachmentRefusal,
  describeAttachmentLimits,
  formatBytes,
} from '@/utils/aiChatAttachments';
import Tooltip from 'primevue/tooltip';

const vTooltip = Tooltip;

/**
 * The shared chat surface: transcript, streaming turn, tool chips, composer,
 * disclaimer. Deliberately DUMB — it does not own the chat state:
 *
 * - The floating widget (AiAssistantWidget) owns a `useAiChat()` at dashboard-
 *   shell level so the transcript survives panel close and route changes.
 * - The full-page view (AiAssistantChatView) owns its own instance.
 *
 * Conversation persistence (in-flight on the backend: `event: conversation`
 * + load/list endpoints) will land in useAiChat, so both hosts pick it up by
 * construction — this component only renders whatever transcript it is given.
 */
const props = defineProps<{
  /** The chat state/api owned by the host (from useAiChat()). */
  chat: UseAiChatReturn;
  /** Tailors the empty-state suggestions; gating already happened in the host. */
  isStudent?: boolean;
}>();

const draft = ref('');
const transcriptEl = ref<HTMLElement | null>(null);

// ─── Attachments (one-turn context; caps mirror the server's) ───

const ACCEPTED_MIMES = AI_ATTACHMENT_LIMITS.mimes;
const attachmentLimits = describeAttachmentLimits();

const fileInput = ref<HTMLInputElement | null>(null);
const pendingFiles = ref<AiChatAttachmentUpload[]>([]);
const attachmentError = ref<string | null>(null);

const prettySize = formatBytes;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.slice(result.indexOf(',') + 1)); // strip data: prefix
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

async function onFilesPicked(event: Event) {
  attachmentError.value = null;
  const picked = Array.from((event.target as HTMLInputElement).files ?? []);
  if (fileInput.value) fileInput.value.value = '';

  for (const file of picked) {
    const refusal = attachmentRefusal(file, pendingFiles.value);
    if (refusal) {
      attachmentError.value = refusal.message;
      if (refusal.stop) break;
      continue;
    }

    try {
      pendingFiles.value = [...pendingFiles.value, {
        name: file.name,
        mime: file.type,
        size: file.size,
        data: await fileToBase64(file),
      }];
    } catch {
      attachmentError.value = `"${file.name}" could not be read.`;
    }
  }
}

function removeFile(index: number) {
  pendingFiles.value = pendingFiles.value.filter((_, i) => i !== index);
  attachmentError.value = null;
}

// Suggestions must read naturally for EVERY school type — no "term"/"JSS 2"
// (secondary) or "semester"/"200 level" (tertiary) wording.
const suggestions = computed(() =>
  props.isStudent
    ? ['What is on my timetable today?', 'Summarise my latest results', 'Do I have any outstanding bills?']
    : ['What can you help me with?', 'Who are our top debtors?', 'Where do I enter student scores?'],
);

const onSend = async(text?: string) => {
  const value = (text ?? draft.value).trim();
  if (!value || props.chat.isStreaming.value) return;
  const attachments = pendingFiles.value;
  draft.value = '';
  pendingFiles.value = [];
  attachmentError.value = null;
  await props.chat.send(value, attachments);
};

const toolChipClass = (status: AiToolStatus) =>
  status === 'error'
    ? 'bg-red-50 border-red-200 text-red-600'
    : status === 'done'
      ? 'bg-green-50 border-green-200 text-green-700'
      : 'bg-blue-50 border-blue-200 text-blue-700';

const toolChipIcon = (status: AiToolStatus) =>
  status === 'error'
    ? 'pi pi-times-circle text-xs'
    : status === 'done'
      ? 'pi pi-check-circle text-xs'
      : 'pi pi-spin pi-spinner text-xs';

// Keep the newest content in view as it streams in. Also fires on mount (the
// drawer host re-mounts this panel on every open), jumping to the latest turn.
watch(
  [() => props.chat.messages.value, () => props.chat.streamingText.value, () => props.chat.toolActivity.value, () => props.chat.error.value],
  async() => {
    await nextTick();
    const el = transcriptEl.value;
    if (el) el.scrollTop = el.scrollHeight;
  },
  { immediate: true },
);
</script>

<style scoped>
.ai-cursor::after {
  content: '▍';
  margin-left: 1px;
  animation: ai-blink 1s steps(2, start) infinite;
  color: var(--p-primary-color, #6366f1);
}

@keyframes ai-blink {
  to {
    visibility: hidden;
  }
}

/* Markdown answer typography — scoped styles don't reach v-html content, so use :deep. */
.ai-markdown :deep(p) {
  margin: 0 0 0.5rem;
}
.ai-markdown :deep(p:last-child) {
  margin-bottom: 0;
}
.ai-markdown :deep(ul),
.ai-markdown :deep(ol) {
  margin: 0.25rem 0 0.5rem;
  padding-left: 1.25rem;
}
.ai-markdown :deep(ul) {
  list-style: disc;
}
.ai-markdown :deep(ol) {
  list-style: decimal;
}
.ai-markdown :deep(h1),
.ai-markdown :deep(h2),
.ai-markdown :deep(h3),
.ai-markdown :deep(h4) {
  font-weight: 600;
  margin: 0.5rem 0 0.25rem;
}
.ai-markdown :deep(h1) { font-size: 1.05rem; }
.ai-markdown :deep(h2) { font-size: 1rem; }
.ai-markdown :deep(h3) { font-size: 0.95rem; }
.ai-markdown :deep(code) {
  background: #f3f4f6;
  border-radius: 4px;
  padding: 0.1rem 0.3rem;
  font-size: 0.85em;
}
.ai-markdown :deep(pre) {
  background: #1f2937;
  color: #f9fafb;
  border-radius: 8px;
  padding: 0.75rem;
  overflow-x: auto;
  margin: 0.5rem 0;
}
.ai-markdown :deep(pre code) {
  background: transparent;
  padding: 0;
  color: inherit;
}
.ai-markdown :deep(blockquote) {
  border-left: 3px solid #e5e7eb;
  padding-left: 0.75rem;
  color: #6b7280;
  margin: 0.5rem 0;
}
.ai-markdown :deep(a) {
  color: var(--p-primary-color, #6366f1);
  text-decoration: underline;
}
</style>
